import { useEffect, useRef } from 'react';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { z } from 'zod';
import { type FormSchema, type UseZodFormOptions, type ZodFormReturn, useZodForm } from '../../components/form';

/**
 * A `useZodForm` that edits a slice of the workflow builder's own state.
 *
 * The builder keeps the workflow in `useWorkflowForm` (dirty detection, local draft, conflicts, the save
 * payload). That state stays the source of truth; this form only adds the schema and the per-field messages.
 *  - every edit is handed to `onFieldChange` (the builder's `updateField` / `updateStep`), valid or not, so what
 *    is saved and when never depends on validation;
 *  - a change that comes from outside (draft restore, conflict reload, another tab of the builder) is written
 *    back into the fields, without a reset, so errors and focus survive typing.
 *
 * `sync: false` seeds the fields once (list editors keyed by step).
 */
export function useBuilderForm<TSchema extends FormSchema>(
  schema: TSchema,
  values: DefaultValues<z.input<TSchema>>,
  onFieldChange: (name: string, all: z.input<TSchema>) => void,
  options: Omit<UseZodFormOptions<TSchema>, 'defaultValues'> & { sync?: boolean } = {},
): ZodFormReturn<TSchema> {
  const { sync = true, ...formOptions } = options;
  const form = useZodForm(schema, { ...formOptions, defaultValues: values });

  const latestHandler = useRef(onFieldChange);
  useEffect(() => {
    latestHandler.current = onFieldChange;
  });

  useEffect(() => {
    const subscription = form.watch((all, info) => {
      if (info.type === 'change' && info.name) latestHandler.current(info.name, all as z.input<TSchema>);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useEffect(() => {
    if (!sync) return;
    for (const [key, value] of Object.entries(values as FieldValues)) {
      if (!Object.is(form.getValues(key as never), value)) form.setValue(key as never, value as never);
    }
  }, [form, sync, values]);

  return form;
}
